<template>
  <div v-if="isUserMessageActive" class="messages-container">
    <div v-for="(msg, index) in messages" :key="index" class="message-wrapper">
      <div class="userMessage">
        <IconMediumAlertWarning class="warningIcon" />
        {{ getMessage(msg) || '' }}
      </div>
      <button class="close-button" @click="closeMessage(msg)">
        <IconX class="closeIcon" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import IconMediumAlertWarning from '@/design-system/assets/icons/IconMediumAlertWarning.vue'
import IconX from '../assets/icons/IconX.vue'
import { useUserMessage } from '@/pinia/userMessage'
import { onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { UserMessage } from '@/interfaces/userMessage.interface'

const userMessageStore = useUserMessage()
const { isUserMessageActive, messages } = storeToRefs(userMessageStore)
const { fetchUserMessage, closeMessage } = userMessageStore
const { locale } = useI18n()
const isLoading = ref(false)

function getMessage(msg: UserMessage): string {
  const lang = locale.value || 'en'
  return msg.message[lang] || msg.message['en']
}

onMounted(async () => {
  isLoading.value = true
  fetchUserMessage()
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
@use '@/styles/variables.scss' as old-variables;

.messages-container {
  position: absolute;
  top: calc(#{variables.$layout-top-bar-height} + 8px);
  z-index: 100;
  width: calc(100% - 100px);
  left: 80px;
  padding: 8px 0;
}

.message-wrapper {
  background-color: old-variables.$color-warning-bg;
  color: old-variables.$color-primary-dark;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 0;
  margin-bottom: 8px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.16);
  border-radius: 8px;
}

.userMessage {
  @include font-mixins.desktop-bold-paragraph;
  color: old-variables.$color-primary-dark;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  gap: 8px;
  margin: 0 auto;
  width: 100%;
}

.close-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  border: none;
  border-radius: 8px;
  gap: 4px;
  margin-right: 8px;
  background-color: old-variables.$color-warning-bg;
  width: 40px;
  height: 32px;
}

.closeIcon {
  width: 16px;
  height: 16px;
}

.warningIcon {
  width: 24px;
  height: 24px;
  min-width: 24px;
}

.close-button:hover {
  background-color: rgba(212, 136, 6, 1);
  width: 40px;
  height: 32px;
  padding: 8px 12px;
  gap: 4px;
}

@media all and (max-width: 1024px) {
  .messages-container {
    width: calc(100% - 32px);
    left: 16px;
    right: 16px;
    margin: 0 auto;
    padding: 8px;
  }
  .message-wrapper {
    width: 100%;
  }
  .userMessage {
    @include font-mixins.mobile-bold-paragraph;
    font-size: 16px;
    margin: 0 auto;
  }
  .closeIcon {
    display: flex;
    justify-content: center;
    align-items: center;
    order: 2;
    width: 24px;
    height: 24px;
  }
  .warningIcon {
    width: 24px;
    height: 24px;
    min-width: 24px;
  }
}
</style>
